export type AppRoute =
  | 'overview'
  | 'unit-overview'
  | 'financial-statements'
  | 'geospatial-intelligence'
  | 'coming-soon';

export type HeaderFilterId = 'currency' | 'period' | 'client' | 'unit';

export interface RouteMeta {
  title: string;
  breadcrumb: string;
  section?: string;
  headerFilters: readonly HeaderFilterId[];
}

export const DEFAULT_APP_ROUTE: AppRoute = 'overview';

export const APP_ROUTES: readonly AppRoute[] = [
  'overview',
  'unit-overview',
  'financial-statements',
  'geospatial-intelligence',
  'coming-soon',
] as const;

export const ROUTE_META: Record<AppRoute, RouteMeta> = {
  overview: {
    title: 'Portfolio',
    breadcrumb: 'Portfolio',
    headerFilters: ['currency', 'period', 'unit', 'client'],
  },
  'unit-overview': {
    title: 'Units',
    breadcrumb: 'Units',
    section: 'Portfolio',
    headerFilters: ['unit', 'client'],
  },
  'financial-statements': {
    title: 'Financial Statements',
    breadcrumb: 'Financial Statements',
    section: 'Portfolio',
    headerFilters: ['unit', 'client'],
  },
  'geospatial-intelligence': {
    title: 'Fractal Swarm',
    breadcrumb: 'Fractal Swarm',
    headerFilters: ['client'],
  },
  'coming-soon': {
    title: 'In The Works',
    breadcrumb: 'In The Works',
    headerFilters: [],
  },
};

export function getRouteMeta(route: AppRoute): RouteMeta {
  return ROUTE_META[route];
}

export function isAppRoute(value: string): value is AppRoute {
  return (APP_ROUTES as readonly string[]).includes(value);
}

/** Parse `window.location.hash` (e.g. `#/unit-overview`) into a route id. */
export function routeFromHash(hash: string): AppRoute {
  const normalized = hash.replace(/^#\/?/, '').trim().toLowerCase();
  if (!normalized) return DEFAULT_APP_ROUTE;
  return isAppRoute(normalized) ? normalized : DEFAULT_APP_ROUTE;
}

export function hashFromRoute(route: AppRoute): string {
  return `#/${route}`;
}

export function readRouteFromWindow(): AppRoute {
  return routeFromHash(window.location.hash);
}

export function writeRouteToWindow(route: AppRoute, replace = false): void {
  const hash = hashFromRoute(route);
  if (window.location.hash === hash) return;

  if (replace) {
    const url = `${window.location.pathname}${window.location.search}${hash}`;
    window.history.replaceState(null, '', url);
    return;
  }

  window.location.hash = hash;
}
